import { getMeta } from '@helpcenter/meta';
// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri, HelpPageAri } from '@atlassian/ari';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	buildPortalPageAri,
	isPortalPage,
} from '@atlassian/company-hub-common/utils/hub-page-utils';
import { createRelayResource } from '@atlassian/help-center-common-util/resource-router';
import HubHomeContentWrapperQuery from '@atlassian/hub-home/HubHomeContentWrapperQueryParameters';
import type { hubHomeContentWrapperQuery } from '@atlassian/hub-home/HubHomeContentWrapperQueryParameters';

const HUB_EXTENSION_MANAGE_TRANSLATIONS = 'hub_extension_manage_translations';
const HUB_TRANSLATIONS_SAVE_DEFAULT_LANG = 'hub_translations_save_default_lang';
const HUBS_TRANSLATION_PORTAL_LANG = 'hubs_translation_portal_lang';
const HELP_HUBS_CONFIGURABLE_SITE_TREE = 'help_hubs_configurable_site_tree';
const HUBS_PREMIUM_UPSELL = 'hubs_premium_upsell';

export const getPageIdFromMatch = (match: {
	params: Record<string, string | null | undefined>;
}): string => {
	if (FeatureGates.checkGate('portal-pages-in-hub')) {
		const portalId = match.params.portalId;
		if (portalId) {
			return `portal_page-${portalId}`;
		}
	}
	return match.params.pageId || '';
};

export const EditLayoutResource = createRelayResource<hubHomeContentWrapperQuery>({
	type: 'EDIT_HUB_LAYOUT',
	getQuery: ({ match }, { cloudId }) => {
		const activationId = getMeta('activation-id') || '';
		const helpCenterId = match.params.helpcenterId || '';
		const pageId = getPageIdFromMatch(match);
		const computedHelpCenterAri = HelpHelpCenterAri.create({
			activationId,
			helpCenterId,
		}).toString();
		const resolvedPageId = pageId || helpCenterId;
		const computedPageAri =
			isPortalPage(resolvedPageId) && helpCenterId
				? buildPortalPageAri(computedHelpCenterAri, resolvedPageId)
				: HelpPageAri.create({ activationId, pageId: resolvedPageId }).toString();
		const isDraft = FeatureGates.checkGate('help_hubs_draft_mode');
		const isConfigurableSiteTreeEnabled = FeatureGates.checkGate(HELP_HUBS_CONFIGURABLE_SITE_TREE);
		// Gates the hub's topic pages query; see hub-home's Topics section.
		const isTopicsEnabled = FeatureGates.checkGate(HUBS_PREMIUM_UPSELL);
		const includeLayoutTranslations = FeatureGates.checkGate(HUB_EXTENSION_MANAGE_TRANSLATIONS);
		const includeTranslatableBlocksSchema = FeatureGates.checkGate(
			HUB_TRANSLATIONS_SAVE_DEFAULT_LANG,
		);
		return {
			parameters: HubHomeContentWrapperQuery,
			variables: {
				cloudId,
				isDraft,
				isConfigurableSiteTreeEnabled,
				isTopicsEnabled,
				includeLayoutTranslations,
				includeTranslatableBlocksSchema,
				isPortalPage:
					FeatureGates.checkGate(HUBS_TRANSLATION_PORTAL_LANG) && isPortalPage(resolvedPageId),
				hubAri: computedHelpCenterAri,
				hubEditMode: true,
				pageAri: computedPageAri,
				// When false the field is not requested, so help-kosh never resolves it and no call
				// reaches convo-ai. The editor gates consumption separately on the help center's own
				// `type`, since this route's `:helpcenterId` is what decides which one is being edited.
				rovoConfigFromKosh: FeatureGates.checkGate('hubs_rovo_config_from_kosh'),
			},
			options: {
				fetchPolicy: 'store-or-network',
			},
		};
	},
});
